/**
 * 관리자 사이드 메뉴 이동 가드 — 상세 페이지가 등록한 가드를 Sidebar에 넘기는 슬롯(D-19).
 *
 * @spec:2026-09-29-처방검토-판정기록-조회화면
 *
 * <BrowserRouter>라 useBlocker를 못 쓴다(현황 6) → Sidebar 선택 prop + 이 컨텍스트로 가로챈다.
 * 상세는 useAdminNavGuard로 마운트 시 등록·언마운트 시 해제한다(나중 등록이 앞 등록 대체).
 * ref 슬롯이라 등록이 재렌더를 만들지 않고, clear는 식별자 확인으로 key={logId} 교체에도 안전(결정 B1).
 * 이 부품은 판정 기록 도메인을 모른다(ARCHITECTURE §2).
 */
import { createContext, useContext, useEffect, useMemo, useRef } from "react";

/** false = 이동 보류(상세가 배너를 띄운다). true = 그대로 이동. */
export type NavGuard = (path: string) => boolean;

export interface NavGuardSlot {
  set(g: NavGuard): void;
  clear(g: NavGuard): void;
  check: NavGuard;
}

export const NavGuardContext = createContext<NavGuardSlot | null>(null);

/** AdminLayout이 만든다 — ref 슬롯 하나(나중 등록이 앞 등록 대체, 식별자 확인 해제). */
export const useNavGuardSlot = (): NavGuardSlot => {
  const slot = useRef<NavGuard | null>(null);
  return useMemo(
    () => ({
      set: (g) => {
        slot.current = g;
      },
      clear: (g) => {
        if (slot.current === g) slot.current = null; // 남의 등록을 지우지 않는다
      },
      check: (path) => (slot.current ? slot.current(path) : true), // 가드 없으면 지금처럼 이동
    }),
    []
  );
};

/** 상세가 부른다 — 최신 가드를 ref로 보고 매 렌더 재등록 없이 effect는 마운트 1회. */
export const useAdminNavGuard = (guard: NavGuard): void => {
  const ctx = useContext(NavGuardContext);
  const latest = useRef(guard);
  latest.current = guard;
  useEffect(() => {
    if (!ctx) return; // AdminLayout 밖(단위 테스트)이면 no-op
    const g: NavGuard = (p) => latest.current(p);
    ctx.set(g);
    return () => ctx.clear(g); // 언마운트 해제(D-19)
  }, [ctx]);
};
